import { ScopeGate } from '@components/feedback/ScopeGate';
import { LocationOnOutlined } from '@mui/icons-material';
import { Button, MenuItem, TextField } from '@mui/material';
import { useAppShellStore } from '@store/appShellStore';
import { useState } from 'react';

interface CentrePickerGateProps {
  title: string;
  description: string;
}

/**
 * "Pick a centre to continue" panel — shown when centres exist but the top bar
 * isn't pointing at one of them (nothing selected, or the read-only "All
 * Centres" rollup). Choosing one persists it and reloads, the same mechanism the
 * top-bar switcher uses, so the whole app re-bootstraps on the new scope.
 */
export const CentrePickerGate = ({ title, description }: CentrePickerGateProps) => {
  const { centres, setCentreId } = useAppShellStore();
  const activeCentres = centres.filter((c) => c.active);
  const [choice, setChoice] = useState('');

  const apply = () => {
    if (!choice) return;
    setCentreId(choice);
    window.location.reload();
  };

  return (
    <ScopeGate Icon={LocationOnOutlined} title={title} description={description}>
      <TextField
        select
        size="small"
        fullWidth
        value={choice}
        onChange={(e) => setChoice(e.target.value)}
        label="Centre"
        sx={{ mt: '4px', maxWidth: 320 }}
      >
        {activeCentres.map((c) => (
          <MenuItem key={c._id} value={c._id} sx={{ fontSize: '13px' }}>{c.name}</MenuItem>
        ))}
        {activeCentres.length === 0 && (
          <MenuItem value="" disabled sx={{ fontSize: '13px' }}>No centres available</MenuItem>
        )}
      </TextField>

      <Button
        onClick={apply}
        disabled={!choice}
        variant="contained"
        sx={(theme) => ({
          bgcolor: theme.palette.primary.main, borderRadius: '8px', fontSize: 13.5, fontWeight: 600,
          px: '20px', py: '9px', textTransform: 'none', boxShadow: 'none',
          '&:hover': { bgcolor: theme.palette.primary.dark, boxShadow: 'none' },
        })}
      >
        Continue
      </Button>
    </ScopeGate>
  );
};
